/**
 * A marca na aba do navegador (07/10/2026, A079): o ícone da aba, o ícone do
 * iPhone e o título. Antes o ícone da aba era sempre o da Eva (fixo no
 * `index.html`), em todas as marcas.
 *
 * Roda duas vezes: no `brand-boot`, antes do React, pela marca do endereço; e
 * no app, quando a marca é resolvida — inclusive a da porta por caminho
 * (`/entrar/<slug>`), que o `brand-boot` não enxerga.
 *
 * O ícone da aba é o ícone do app gerado pelo editor (já encaixado no
 * quadrado, sem achatar) ou, sem ele, o símbolo da marca encaixado aqui
 * mesmo, num quadrado transparente — a mesma pegada do avatar (A080): um
 * símbolo largo diminui para caber inteiro, em vez de o navegador espremê-lo.
 * Sem nenhum dos dois, fica o da Eva.
 */
const ICONE_DA_EVA = '/logo/icon.png';

interface MarcaParaAba {
  name?: string | null;
  branding?: {
    simboloUrl?: string | null;
    pwaIcons?: { icon192?: string | null; iosIcon?: string | null } | null;
  } | null;
}

function trocarHref(seletor: string, href: string) {
  const link = document.querySelector<HTMLLinkElement>(seletor);
  if (link && link.getAttribute('href') !== href) link.setAttribute('href', href);
}

/**
 * Encaixa a imagem num quadrado transparente, mantendo a proporção, e devolve
 * como data URL. `null` se não der para ler a imagem (CORS) — aí vai a URL.
 */
function encaixarNoQuadrado(url: string, lado = 64): Promise<string | null> {
  return new Promise((resolve) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onerror = () => resolve(null);
    img.onload = () => {
      try {
        const canvas = document.createElement('canvas');
        canvas.width = lado;
        canvas.height = lado;
        const ctx = canvas.getContext('2d');
        if (!ctx) return resolve(null);
        const escala = Math.min(lado / img.naturalWidth, lado / img.naturalHeight);
        const w = img.naturalWidth * escala;
        const h = img.naturalHeight * escala;
        ctx.drawImage(img, (lado - w) / 2, (lado - h) / 2, w, h);
        resolve(canvas.toDataURL('image/png'));
      } catch {
        resolve(null);
      }
    };
    img.src = url;
  });
}

let ultimaFonte: string | null = null;

export function aplicarMarcaNaAba(marca: MarcaParaAba | null | undefined): void {
  if (typeof document === 'undefined' || !marca) return;
  const b = marca.branding;
  const gerado = b?.pwaIcons?.icon192;
  if (gerado) {
    ultimaFonte = gerado;
    trocarHref('link#favicon', gerado);
  } else if (b?.simboloUrl) {
    const fonte = b.simboloUrl;
    ultimaFonte = fonte;
    void encaixarNoQuadrado(fonte).then((dados) => {
      // Só aplica se a marca não mudou enquanto a imagem carregava.
      if (ultimaFonte === fonte) trocarHref('link#favicon', dados ?? fonte);
    });
  } else {
    ultimaFonte = null;
    trocarHref('link#favicon', ICONE_DA_EVA);
  }
  trocarHref('link#apple-icon', b?.pwaIcons?.iosIcon || b?.simboloUrl || ICONE_DA_EVA);
  if (marca.name) {
    document.title = marca.name;
    nomeNoIphone(marca.name);
  }
}

/**
 * O nome que o iPhone sugere em "Adicionar à Tela de Início" (15/10/2026).
 * O Safari dá preferência a esta meta e a lê na hora de adicionar; sem ela,
 * usava o manifest lido ao montar a página — o da Eva, antes de o app trocar o
 * link —, e o Instituto do Luto Parental instalava como "Eva".
 */
function nomeNoIphone(nome: string) {
  let meta = document.querySelector<HTMLMetaElement>('meta[name="apple-mobile-web-app-title"]');
  if (!meta) {
    meta = document.createElement('meta');
    meta.name = 'apple-mobile-web-app-title';
    document.head.appendChild(meta);
  }
  if (meta.content !== nome) meta.content = nome;
}
